import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface Level {
    user_level_id: number;
    user_level_name: string;
    user_level_notes: string;
    user_level_is_locked: number;
    user_count: number;
    role_ids: number[];
}

interface Role {
    user_role_id: number;
    user_role_label: string;
    user_role_description: string;
    subs?: Role[];
}

const API = environment.api + 'roles-permissions';

/**
 * Settings > Users & Permissions > Roles & Permissions (mockup settings #permissionsPanel; twin-ci
 * users_levels_*). Administrators only (the API checks it too). The mockup's role cards and
 * permissions grid show the real levels: built-in (locked) levels have fixed access and cannot be
 * changed; custom levels are what staff users get, with the permissions ticked here.
 */
@Component({
    selector: 'app-roles-permissions',
    templateUrl: './roles-permissions.component.html',
    styleUrls: ['./roles-permissions.component.scss']
})
export class RolesPermissionsComponent implements OnInit {
    levels: Level[] = [];
    roles: Role[] = [];
    loaded = false;
    message = '';
    error = '';

    editing: { user_level_id: number, user_level_name: string, user_level_notes: string } = null;
    ticked: { [roleId: number]: boolean } = {};
    saving = false;
    formError = '';
    fieldErrors: { [field: string]: string } = {};
    deleting: Level = null;

    constructor(private http: HttpClient) { }

    ngOnInit() {
        this.load();
    }

    private text(err: any, fallback: string) {
        return typeof err === 'string' ? err : fallback;
    }

    private load() {
        this.http.get<any>(API).subscribe(
            res => {
                this.levels = res.levels || [];
                this.roles = res.roles || [];
                this.loaded = true;
            },
            err => this.error = this.text(err, 'Roles and permissions could not be loaded.')
        );
    }

    get customLevels(): Level[] {
        return this.levels.filter(l => !l.user_level_is_locked);
    }

    /** Permission rows for the grid: each menu permission followed by its sub-permissions. */
    get rows(): { role: Role, sub: boolean }[] {
        const rows = [];
        this.roles.forEach(r => {
            rows.push({ role: r, sub: false });
            (r.subs || []).forEach(s => rows.push({ role: s, sub: true }));
        });
        return rows;
    }

    has(level: Level, role: Role) {
        return level.role_ids.indexOf(role.user_role_id) !== -1;
    }

    add() {
        this.open({ user_level_id: 0, user_level_name: '', user_level_notes: '' }, []);
    }

    edit(l: Level) {
        this.open({ user_level_id: l.user_level_id, user_level_name: l.user_level_name, user_level_notes: l.user_level_notes }, l.role_ids);
    }

    private open(level, roleIds: number[]) {
        this.editing = level;
        this.ticked = {};
        roleIds.forEach(id => this.ticked[id] = true);
        this.formError = '';
        this.fieldErrors = {};
    }

    close() {
        this.editing = null;
    }

    /** twin-ci switch_tl: ticking a sub-permission ticks its menu permission too. */
    tickSub(parent: Role, sub: Role, on: boolean) {
        this.ticked[sub.user_role_id] = on;
        if (on) {
            this.ticked[parent.user_role_id] = true;
        }
    }

    tickAll(on: boolean) {
        this.ticked = {};
        if (on) {
            this.rows.forEach(r => this.ticked[r.role.user_role_id] = true);
        }
    }

    get tickedCount() {
        return Object.keys(this.ticked).filter(k => this.ticked[k]).length;
    }

    save() {
        const e: { [field: string]: string } = {};
        if (!this.editing.user_level_name.trim()) { e.user_level_name = 'Level Name is required.'; }
        if (!this.editing.user_level_notes.trim()) { e.user_level_notes = 'Level Description is required.'; }
        this.fieldErrors = e;
        this.formError = '';
        if (Object.keys(e).length) {
            return;
        }
        const body = {
            user_level_name: this.editing.user_level_name.trim(),
            user_level_notes: this.editing.user_level_notes.trim(),
            role_ids: Object.keys(this.ticked).filter(k => this.ticked[k]).map(Number),
        };
        this.saving = true;
        const id = this.editing.user_level_id;
        const request = id ? this.http.put<any>(API + '/' + id, body) : this.http.post<any>(API, body);
        request.subscribe(
            res => {
                this.saving = false;
                this.message = res.message;
                this.close();
                this.load();
            },
            err => {
                this.saving = false;
                this.formError = this.text(err, 'The level could not be saved.');
            }
        );
    }

    remove() {
        this.saving = true;
        this.http.delete<any>(API + '/' + this.deleting.user_level_id).subscribe(
            res => {
                this.saving = false;
                this.deleting = null;
                this.message = res.message;
                this.load();
            },
            err => {
                this.saving = false;
                this.deleting = null;
                this.error = this.text(err, 'The level could not be deleted.');
            }
        );
    }
}
